import React, { useState, useEffect, useMemo } from 'react';
import { Layout, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import WebSocketManager from '../utils/WebSocketManager';
import TopNav from './TopNav';
import UploadSection from './UploadSection';
import ReportInfo from './ReportInfo';
import PreviewAndResult from './PreviewAndResult';
import useSetTitle from './GlobalSetTitleHook';

const { Header, Content } = Layout;
export interface reportDataProps {
  reportNumber?: string,
  titleCompany?: string,
  address?: string,
  time?: string,
  cmaChecked?: boolean,
  cnasChecked?: boolean,
}
interface rulesProps {
  firstPageCount?: number | string, // 首页
  lastPageCount?: number | string, // 尾页
  testResultCount?: number | string, // 测试结果
  contentCount?: number | string, // 正文
  totalCount?: number | string,
  othersCount?: number | string// 其他
}

interface AuditIssue {
  target_type: string;
  target_id: string;
  index: number;
  key_text: string;
  issue_description: string;
  recommendation: string;
  need_update: string;
  change_text: string;
}

const AuditDashboard: React.FC = () => {
  useSetTitle()
  const [uploadedReport, setUploadedReport] = useState<File | string | null>(null);
  const [uploadedApplication, setUploadedApplication] = useState<File | null>(null);
  const [auditResults, setAuditResults] = useState<Record<string, string>[]>([]);
  const [auditProgress, setAuditProgress] = useState(0);
  const [rules, setRules] = useState<rulesProps>({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [downloadFile, setDownloadFile] = useState<{url?: File | string | null, name?: string}>({});
  const [reportData, setReportData] = useState<reportDataProps>({});
  const [application, setApplication] = useState<Record<string, any>>({});
  const [applicating, setApplicating] = useState(false);

  const token = localStorage.getItem('token');
  const wsManager = useMemo(() => new WebSocketManager(), []);

  const composeAuditResult = (result: string): string => {
    try {
      const issues: AuditIssue[] = JSON.parse(result);
      let returnVal: any = JSON.parse(JSON.stringify((issues.map((issue) => (
        `审核内容：${issue.issue_description}  \n 审核结果： ${issue.need_update === "true" ? "审核不通过" : "审核通过"}  \n建议：${issue.recommendation}`
      )))))// 用两个换行分隔不同问题
      if (returnVal.length > 0) {
        return returnVal.join('\n\n');
      } else {
        return "本页审核通过";
      }
    }
    catch (error) {
      console.error('Failed to parse audit result:', error);
      return result;
    }
  }
  
  useEffect(() => {
    const fetchRules = async () => {
      try {
        // const response = await api.get('/api/rules/');
        const response = await api.get('/api/rule-summary/');
          const rules = response.data
          setRules(rules);
      } catch (error) {
        console.error('Failed to fetch rules:', error);
      }
    };
    fetchRules();
  }, [])
  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    wsManager.setOnMessageCallback((data) => {
      if (data.result) {
        const obj = {
          resultDateTime: new Date().toLocaleTimeString(),
          result: !uploadedReport ? composeAuditResult(data.result) : data.result,
        }
        setAuditResults((prev) => [...prev, obj]);
        if (!uploadedReport) {
          try {
            const app =  typeof data.result === 'string' ? JSON.parse(data.result) : data.result
            setApplication(app)
            setApplicating(false)
          } catch (error) {
            console.error('Failed to parse application:', error);
          }
        }
      }
      const audit_file = data.audit_file || data.result?.audit_file || ''
      const download_file = data.download_file || data.result?.download_file || ''
      const report_info  = data.report_info || data.result?.report_info || ""
      if (audit_file) {
        setUploadedReport(audit_file)
      }
      if (download_file) {
        // setDownloadFile({...downloadFile, url: download_file});
        setDownloadFile(prev => ({ ...prev, url: download_file }));
      }
      if (report_info && JSON.stringify(report_info) !== '{}') {
        const report_info_obj = typeof report_info === 'string' ? JSON.parse(report_info) : report_info
        setReportData({
          ...report_info_obj,
          time: report_info_obj.reportDate,
          cmaChecked: report_info_obj.hasCma,
          cnasChecked: report_info_obj.hasCnas,
        })
      }
      if (data.progress !== undefined) {
        setAuditProgress(data.progress);
      }
      setLoading(false);
    });

    wsManager.setOnErrorCallback((msg) => {
      message.error(msg);
      setLoading(false);
    });

    setLoading(true);
    wsManager.connect(token);

    return () => {
      wsManager.close(true); // 清理 WebSocket 连接
    };
  }, [token, navigate, wsManager]);

  const getBase64DataURL = (file: File, callback: (dataURL: string) => void) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result) {
        const base64Data = (reader.result as string).split(',')[1];
        const mimeType = file.type || 'application/octet-stream';
        const dataURL = `data:${mimeType};base64,${base64Data}`;
        callback(dataURL);
      }
    };
    reader.onerror = () => {
      message.error('文件读取失败，请检查文件格式。');
    };
    reader.readAsDataURL(file);
  };

  const handleReportUpload = (file: File) => {
    setUploadedApplication(null);
    setUploadedReport(file);
    setDownloadFile({name: JSON.parse(JSON.stringify(file.name)), url: null});
    setReportData({})
    setAuditProgress(0)
    console.log('Report uploaded:', file.name);
  };

  const handleAppFormUpload = (file: File) => {
    setUploadedReport(null)
    setUploadedApplication(file);
    setDownloadFile({});
    setReportData({})
    setAuditProgress(0)
    console.log('Application uploaded:', file.name);
  };

  const handleAppForm = () => {
    if (!uploadedApplication) {
      message.error('请先上传申请表！');
      return;
    }
    // 实现申请表处理逻辑
    const options = {
        data_type: 'app-form',
        auditOptions: { cmaChecked: false, cnasChecked: false },
      };
      
      setAuditResults([]); // 重置结果

      // Send application file as base64 (if available)
      getBase64DataURL(uploadedApplication, (dataURL) => {
        const message = {
          options,
          data: dataURL,
        };
        console.log('Sending report:', message);
        setApplicating(true)
        wsManager.send(JSON.stringify(message));
      });

      setAuditProgress(10);
  };

  const handleStartAudit = (no_application_form?: boolean) => {
    if (!uploadedReport) {
      message.error('请先上传报告文件！');
      return;
    }

    setLoading(true);
    setAuditResults([]); // 重置结果
    setAuditProgress(0);

    const options = {
        data_type: 'report',
        auditOptions: { cmaChecked: false, cnasChecked: false },
      };

    // Send report file as binary
    if (typeof uploadedReport !== 'string') getBase64DataURL(uploadedReport, (dataURL) => {
        const message = {
          options,
          data: dataURL,
          no_application_form
        };
        console.log('Sending report:', message);
        wsManager.send(JSON.stringify(message));
      });
  
      setAuditProgress(10);
  };

  return (
    <Layout className="layout">
        <Header style={{ background: '#f0f2f5', padding: 0 }}>
            <TopNav/>
        </Header>
        <Content>
            <UploadSection
                onReportUpload={handleReportUpload}
                onApplicationUpload={handleAppFormUpload}
                onProcessAppForm={handleAppForm}
                onStartAudit={handleStartAudit}
                application={application}
                applicating={applicating}
            />
            <ReportInfo reportData={{...reportData, rules: rules}} />
            <PreviewAndResult downloadUrl={downloadFile.url || ''} downloadName={downloadFile.name || ''} file={uploadedReport || uploadedApplication} auditResults={auditResults} auditProgress={auditProgress} />
        </Content>
    </Layout>
  );
};

export default AuditDashboard;